import { apiRequest } from "@/shared/lib/apiClient";
import { getAuthToken } from "../auth";
import type {
  AuthUser,
  CompanyAdmissionApi,
  CompanyAdmissionDetail,
  CompanyAdmissionsQuery,
  CompanyBillingInvoice,
  CompanyBeneficiary,
  CompanyBeneficiaryDetail,
  CompanyDashboardApiResponse,
  CompanyEmployee,
  CompanyHospital,
  CompanyInvoiceDetail,
  CompanyProfileApi,
  LoginResponse,
} from "./types";

export type LoginPayload = {
  email: string;
  password: string;
  role?: string;
};

export async function loginRequest(payload: LoginPayload): Promise<LoginResponse> {
  return apiRequest<LoginResponse>("/auth/login", {
    method: "POST",
    body: payload,
  });
}

export async function meRequest(): Promise<{ user: AuthUser }> {
  return apiRequest<{ user: AuthUser }>("/auth/me", { auth: true });
}

export async function updateAuthProfileRequest(payload: {
  username: string;
  email: string;
}): Promise<{ user: AuthUser; message?: string }> {
  return apiRequest<{ user: AuthUser; message?: string }>("/auth/profile", {
    method: "PUT",
    auth: true,
    body: payload,
  });
}

export async function updateAuthPasswordRequest(payload: {
  current_password: string;
  password: string;
  password_confirmation: string;
}): Promise<{ message?: string }> {
  return apiRequest<{ message?: string }>("/auth/password", {
    method: "PUT",
    auth: true,
    body: payload,
  });
}

export async function logoutRequest(): Promise<void> {
  await apiRequest<{ message: string }>("/auth/logout", {
    method: "POST",
    auth: true,
  });
}

export type DashboardQuery = {
  date_start?: string;
  date_end?: string;
  hospital_id?: number;
};

function dashboardQueryString(query: DashboardQuery = {}): string {
  const params = new URLSearchParams();
  if (query.date_start) params.set("date_start", query.date_start);
  if (query.date_end) params.set("date_end", query.date_end);
  if (query.hospital_id) params.set("hospital_id", String(query.hospital_id));
  return params.toString();
}

export async function fetchCompanyDashboard(
  query: DashboardQuery = {},
): Promise<CompanyDashboardApiResponse> {
  const qs = dashboardQueryString(query);
  return apiRequest<CompanyDashboardApiResponse>(
    `/company/dashboard${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export type CompanyDashboardDetailResponse<T> = {
  period: CompanyDashboardApiResponse["period"];
  period_label: string;
  data: T;
};

export type VisitRankingFull = {
  agents: Array<{ name: string; cnt: number }>;
  spouses: Array<{ name: string; cnt: number }>;
  children: Array<{ name: string; cnt: number }>;
};

export type AgePyramidDetail = {
  as_of?: string | null;
  total_with_birthdate?: number;
  total_without_birthdate?: number;
  buckets?: Array<{ label: string; count: number; pct: number }>;
  beneficiaries?: Array<{
    name: string;
    kind: string;
    employee_name?: string;
    birthdate?: string | null;
    age?: number | null;
    bucket?: string;
  }>;
};

export async function fetchCompanyDashboardVisitsDetail(
  query: DashboardQuery = {},
): Promise<CompanyDashboardDetailResponse<VisitRankingFull>> {
  const qs = dashboardQueryString(query);
  return apiRequest<CompanyDashboardDetailResponse<VisitRankingFull>>(
    `/company/dashboard/analytics/visits${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchCompanyDashboardAgePyramidDetail(
  query: DashboardQuery = {},
): Promise<CompanyDashboardDetailResponse<AgePyramidDetail>> {
  const qs = dashboardQueryString(query);
  return apiRequest<CompanyDashboardDetailResponse<AgePyramidDetail>>(
    `/company/dashboard/analytics/age-pyramid${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchCompanyHospitals(): Promise<CompanyHospital[]> {
  const response = await apiRequest<{ hospitals: CompanyHospital[] }>("/company/hospitals", {
    auth: true,
  });
  return response.hospitals ?? [];
}

export type CompanyInvoicesQuery = {
  status?: "published" | "paid" | "approved";
};

export async function fetchCompanyInvoices(
  query: CompanyInvoicesQuery = {},
): Promise<CompanyBillingInvoice[]> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);

  const qs = params.toString();
  const response = await apiRequest<{ invoices: CompanyBillingInvoice[] }>(
    `/company/invoices${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
  return response.invoices ?? [];
}

export async function fetchCompanyInvoice(id: number): Promise<CompanyInvoiceDetail> {
  const response = await apiRequest<{ invoice: CompanyInvoiceDetail }>(
    `/company/invoices/${id}`,
    { auth: true },
  );
  return response.invoice;
}

export async function downloadCompanyBillingAttachment(
  attachmentId: number,
  fileName?: string,
): Promise<void> {
  const { getApiBaseUrl } = await import("@/shared/lib/apiClient");
  const token = getAuthToken();
  const response = await fetch(
    `${getApiBaseUrl()}/company/billing/attachments/${attachmentId}/download`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (!response.ok) {
    let detail = "Téléchargement impossible.";
    try {
      const payload = (await response.json()) as { message?: string };
      if (payload.message) detail = payload.message;
    } catch {
      const text = await response.text();
      if (text) detail = text;
    }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || `piece-jointe-${attachmentId}`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function fetchCompanyEmployees(search?: string): Promise<CompanyEmployee[]> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);

  const qs = params.toString();
  const response = await apiRequest<{ employees: CompanyEmployee[]; total: number }>(
    `/company/employees${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
  return response.employees ?? [];
}

export type CreateCompanyEmployeePayload = {
  employee_name: string;
  matricule_company: string;
  birthdate: string;
  gender: "M" | "F";
  phone?: string;
  email?: string;
  city?: string;
  province?: string;
  position?: string;
  hospital_id?: number;
  hospital_ids?: number[];
  primary_hospital_id?: number;
  family_members?: Array<{
    name: string;
    relationship?: string;
    birthdate?: string;
    gender?: "M" | "F";
  }>;
};

export type UpdateCompanyEmployeePayload = CreateCompanyEmployeePayload & {
  matricule?: string;
  status?: "active" | "inactive";
  antenne_id?: number;
  family_members?: Array<{
    id?: number;
    name: string;
    relationship?: string;
    birthdate?: string;
    gender?: "M" | "F";
    status?: "active" | "inactive";
  }>;
};

function appendEmployeeFormFields(
  form: FormData,
  payload: CreateCompanyEmployeePayload | UpdateCompanyEmployeePayload,
) {
  form.append("employee_name", payload.employee_name);
  form.append("matricule_company", payload.matricule_company);
  if (payload.birthdate) form.append("birthdate", payload.birthdate);
  if (payload.gender) form.append("gender", payload.gender);
  if ("matricule" in payload && payload.matricule) form.append("matricule", payload.matricule);
  if (payload.phone) form.append("phone", payload.phone);
  if (payload.email) form.append("email", payload.email);
  if (payload.city) form.append("city", payload.city);
  if (payload.province) form.append("province", payload.province);
  if (payload.position) form.append("position", payload.position);
  if (payload.hospital_id) form.append("hospital_id", String(payload.hospital_id));
  if (payload.primary_hospital_id) {
    form.append("primary_hospital_id", String(payload.primary_hospital_id));
  }
  if (payload.hospital_ids?.length) {
    form.append("hospital_ids", JSON.stringify(payload.hospital_ids));
  }
  if ("status" in payload && payload.status) form.append("status", payload.status);
  form.append("family_members", JSON.stringify(payload.family_members ?? []));
}

export async function createCompanyEmployee(
  payload: CreateCompanyEmployeePayload,
  photo?: File | null,
  familyPhotos?: Array<File | null | undefined>,
): Promise<CompanyEmployee> {
  const hasFamilyPhotos = (familyPhotos ?? []).some((f) => f instanceof File);
  if (photo || hasFamilyPhotos) {
    const form = new FormData();
    appendEmployeeFormFields(form, payload);
    if (photo) form.append("photo", photo);
    (familyPhotos ?? []).forEach((file, index) => {
      if (file instanceof File) form.append(`family_photos[${index}]`, file);
    });

    const response = await apiRequest<{ employee: CompanyEmployee }>("/company/employees", {
      method: "POST",
      auth: true,
      body: form,
    });
    return response.employee;
  }

  const response = await apiRequest<{ employee: CompanyEmployee }>("/company/employees", {
    method: "POST",
    auth: true,
    body: payload,
  });
  return response.employee;
}

export async function fetchCompanyEmployee(id: number): Promise<CompanyEmployee> {
  const response = await apiRequest<{ employee: CompanyEmployee }>(`/company/employees/${id}`, {
    auth: true,
  });
  return response.employee;
}

export async function updateCompanyEmployee(
  id: number,
  payload: UpdateCompanyEmployeePayload,
  photo?: File | null,
  familyPhotos?: Array<File | null | undefined>,
): Promise<CompanyEmployee> {
  const hasFamilyPhotos = (familyPhotos ?? []).some((f) => f instanceof File);
  if (photo || hasFamilyPhotos) {
    const form = new FormData();
    appendEmployeeFormFields(form, payload);
    if (photo) form.append("photo", photo);
    (familyPhotos ?? []).forEach((file, index) => {
      if (file instanceof File) form.append(`family_photos[${index}]`, file);
    });
    form.append("_method", "PUT");

    const response = await apiRequest<{ employee: CompanyEmployee }>(`/company/employees/${id}`, {
      method: "POST",
      auth: true,
      body: form,
    });
    return response.employee;
  }

  const response = await apiRequest<{ employee: CompanyEmployee }>(`/company/employees/${id}`, {
    method: "PUT",
    auth: true,
    body: payload,
  });
  return response.employee;
}

export async function fetchCompanyBeneficiaries(search?: string): Promise<CompanyBeneficiary[]> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);

  const qs = params.toString();
  const response = await apiRequest<{ beneficiaries: CompanyBeneficiary[]; total: number }>(
    `/company/beneficiaries${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
  return response.beneficiaries ?? [];
}

export async function fetchCompanyBeneficiary(
  type: "employee" | "dependant",
  id: number,
): Promise<CompanyBeneficiaryDetail> {
  const response = await apiRequest<{ beneficiary: CompanyBeneficiaryDetail }>(
    `/company/beneficiaries/${type}/${id}`,
    { auth: true },
  );
  return response.beneficiary;
}

function buildAdmissionsQuery(params: CompanyAdmissionsQuery): string {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set("status", params.status);
  if (params.date_start) searchParams.set("date_start", params.date_start);
  if (params.date_end) searchParams.set("date_end", params.date_end);
  if (params.search) searchParams.set("search", params.search);
  if (params.hospital_id) searchParams.set("hospital_id", String(params.hospital_id));
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

export async function fetchCompanyAdmissions(
  query: CompanyAdmissionsQuery = {},
): Promise<{ admissions: CompanyAdmissionApi[]; total: number }> {
  return apiRequest<{ admissions: CompanyAdmissionApi[]; total: number }>(
    `/company/admissions${buildAdmissionsQuery(query)}`,
    { auth: true },
  );
}

export async function fetchCompanyAdmission(id: number): Promise<CompanyAdmissionDetail> {
  const response = await apiRequest<{ admission: CompanyAdmissionDetail }>(
    `/company/admissions/${id}`,
    { auth: true },
  );
  return response.admission;
}

export async function fetchCompanyProfile(): Promise<CompanyProfileApi> {
  const response = await apiRequest<{ company: CompanyProfileApi }>("/company/profile", {
    auth: true,
  });
  return response.company;
}

export type UpdateCompanyProfilePayload = {
  name: string;
  address?: string;
  phone?: string;
};

export async function updateCompanyProfile(
  payload: UpdateCompanyProfilePayload,
): Promise<CompanyProfileApi> {
  const response = await apiRequest<{ company: CompanyProfileApi; message?: string }>(
    "/company/profile",
    {
      method: "PUT",
      auth: true,
      body: payload,
    },
  );
  return response.company;
}
